<!--@format-->
<template>
    <div class="landing-page-settings-widget">
        <loading-card :is-loading="isLoading" />
        <div class="widget-header">
            <h3>{{ title }}</h3>
            <div class="svg-container widget-close-button" @click="closeWidget()">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div v-if="!isLoading">
                <div v-if="currentStep === 'detailSettingsStep'">
                    <detail-settings-step
                        v-if="domains.length > 0"
                        ref="detailSettingsStep"
                        :v="$v"
                        :is-edit-mode="isEditMode"
                        :form="form"
                        :account-id="accountId"
                        :domains="domains"
                    />
                    <div v-else>
                        <landing-page-domains-board-no-records-card
                            no-records-content="You haven't added any domains yet, click below to add a domain so you can start publishing your landing pages."
                            @click="handleCreateDomain"
                        />
                    </div>
                </div>
                <headers-footers-step
                    v-else-if="currentStep === 'headersFootersStep'"
                    ref="headersFootersStep"
                    :form="form"
                    :section-context="sectionContext"
                    :account-id="accountId"
                    :client-id="clientId"
                />
            </div>
        </div>
        <div class="widget-footer">
            <div class="actions-container">
                <app-button class="transparent-btn" @click="closeWidget">Cancel</app-button>
            </div>
            <div class="actions-container">
                <app-button v-if="hasBackAction" class="secondary-btn" @click="goBack">
                    <span class="fa fa-arrow-left"></span>
                    {{ backLabel }}
                </app-button>
                <app-button :disabled="!isFormValid" @click="goNext">
                    {{ nextLabel }}
                    <span class="fa fa-arrow-right"></span>
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { required } from 'vuelidate/lib/validators';
import { isValidSlug } from '@/scripts/validations';
import { LandingPageService } from '@/scripts/services/landing-page/landing-page-service';
import { DomainService } from '@/scripts/services/domain/domain-service';
import { LogLevel } from '@/scripts/logger';

const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const AppButton = () => import('@/components/common/app-button');
const DetailSettingsStep = () =>
    import('@/components/sliding-widgets/landing-page/settings/steps/detail-settings-step');
const HeadersFooterStep = () =>
    import('@/components/sliding-widgets/landing-page/settings/steps/headers-footers-step');
const LoadingCard = () => import('@/components/common/loading-card');
const LandingPageDomainsBoardNoRecordsCard = () =>
    import(
        '@/components/landing-page/landing-page-domains-board/landing-page-domains-board-no-records-card'
    );

export default {
    name: 'landing-page-settings-widget',

    components: {
        'app-button': AppButton,
        'detail-settings-step': DetailSettingsStep,
        'headers-footers-step': HeadersFooterStep,
        'loading-card': LoadingCard,
        'widget-close-svg': WidgetCloseSvg,
        'landing-page-domains-board-no-records-card': LandingPageDomainsBoardNoRecordsCard,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            form: {
                // Step 1
                landingPageName: null,
                metaTitle: null,
                favIconUrl: null,
                domain: null,
                slug: null,
                categories: [],
                hasBranding: null,
                hideFromSearchEngines: false,
                // Step 2
                selectedHeader: undefined,
                selectedFooter: undefined,
                // Edit
                isPublished: false,
                url: null,
                modifiedBy: {
                    name: null,
                    profileImageUrl: null,
                },

                // Template
                config: null,

                updatedDate: null,
                createdDate: null,
                publishedDate: null,
            },

            sectionContext: {
                header: {
                    id: null,
                    hasChanges: false,
                },

                footer: {
                    id: null,
                    hasChanges: false,
                },
            },

            isLoading: false,

            domains: [],

            currentStep: 'detailSettingsStep',

            steps: {
                detailSettingsStep: {
                    title: 'Landing Page Settings',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.getFormDataFromCurrentStep().then(() =>
                                    this.goToStep('headersFootersStep'),
                                );
                            },
                        },
                    },
                },

                headersFootersStep: {
                    title: 'Landing Page Settings',
                    actions: {
                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.getFormDataFromCurrentStep().then(() => {
                                    this.goToStep('detailSettingsStep');
                                });
                            },
                        },

                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                if (this.isEditMode) {
                                    this.handlePatchLandingPage();
                                } else {
                                    this.getFormDataFromCurrentStep().then(() => {
                                        this.createLandingPage();
                                    });
                                }
                            },
                        },
                    },
                },
            },
        };
    },

    validations: {
        form: {
            landingPageName: {
                required,
            },

            domain: {
                required,
            },

            slug: {
                required,
                isValidSlug,
            },
        },
    },

    computed: {
        title() {
            return this.steps[this.currentStep].title;
        },

        hasBackAction() {
            return this.steps[this.currentStep].actions.backAction !== undefined;
        },

        isFormValid() {
            return !this.$v.$invalid;
        },

        isEditMode() {
            return this.widget.landingPageId !== null && this.widget.landingPageId !== undefined;
        },

        isTemplateMode() {
            return (
                this.widget.landingPageTemplateId !== null &&
                this.widget.landingPageTemplateId !== undefined
            );
        },

        backLabel() {
            return this.steps[this.currentStep].actions.backAction.label;
        },

        goBack() {
            return this.steps[this.currentStep].actions.backAction.callback;
        },

        nextLabel() {
            return this.steps[this.currentStep].actions.nextAction.label;
        },

        goNext() {
            return this.steps[this.currentStep].actions.nextAction.callback;
        },

        accountId() {
            return this.$store.state.context.account.id;
        },

        clientId() {
            return this.$store.state.context.client.id;
        },

        baseUrl() {
            return this.$store.getters.baseUrl;
        },
    },

    async created() {
        this.isLoading = true;
        if (this.isEditMode) {
            await this.getEditLandingPageData();
        }
        if (this.isTemplateMode) {
            await this.getLandingPageTemplateData();
        }
        await this.getDomains();
        this.isLoading = false;

        EventBus.$on('landing-page-creation:edit-requested', this.closeWidget);
    },

    beforeDestroy() {
        EventBus.$off('landing-page-creation:edit-requested', this.closeWidget);
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleCreateDomain() {
            EventBus.$emit('close-widget', this.widget);
            this.$router.push(`${this.baseUrl}/LandingPages/Domains`);
            EventBus.$emit('open-widget', {
                name: 'domain-widget',
            });
        },

        // Creation

        goToStep(step) {
            this.currentStep = step;
        },

        async getDomains() {
            const filter = `Validated eq true and AccountId eq ${this.accountId}`;

            try {
                const { data } = await DomainService.getAll({ filter });
                this.domains = data.value;
            } catch (error) {
                this.$helpers.showFailNotification('Error Trying to Obtain the Domains', { error });
            }
        },

        getFormDataFromCurrentStep() {
            return new Promise((resolve) => {
                this.$refs[this.currentStep].next().then((stepFormData) => {
                    this.form = { ...this.form, ...stepFormData };
                    resolve();
                });
            });
        },

        uploadFavicon() {
            return new Promise((resolve, reject) => {
                const base64RegExp = /data:image\/[^;]+;base64[^"]+/;
                if (this.form.favIconUrl && this.form.favIconUrl.match(base64RegExp)) {
                    const file = {
                        name: `${this.form.landingPageName
                            .toLowerCase()
                            .replace(' ', '-')}-lp-favicon`,

                        url: this.form.favIconUrl,
                    };

                    this.$api
                        .post('/api/Files', file)
                        .then((response) => {
                            this.form.favIconUrl = response.data;
                            resolve();
                        })
                        .catch(reject);
                } else {
                    resolve();
                }
            });
        },

        submitLandingPageForm() {
            return new Promise((resolve, reject) => {
                const request = {
                    Name: this.form.landingPageName,
                    MetaTitle: this.form.metaTitle,
                    FaviconUrl: this.form.favIconUrl,
                    HideFromSearchEngines: this.form.hideFromSearchEngines,
                    HeaderId: this.form.selectedHeader.value,
                    FooterId: this.form.selectedFooter.value,
                    Slug: this.form.slug,
                    DomainId: this.form.domain.id,
                    Categories: this.form.categories,
                    HasBranding: false,
                    AccountId: this.accountId,
                };

                if (this.form.config) {
                    request.TemplateJson = this.form.config;
                }

                LandingPageService.post(request)
                    .then(({ data: { Id } }) => {
                        this.$helpers.showSuccessNotification('Landing page created successfully.');
                        this.closeWidget();
                        this.$router.push(`/LandingPages/${Id}/Design`);
                    })
                    .catch(reject);
            });
        },

        createLandingPage() {
            this.isLoading = true;
            this.uploadFavicon()
                .then(this.submitLandingPageForm)
                .catch((error) => {
                    const message = error.response.data.Message;

                    if (message.includes('name already exists')) {
                        this.$helpers.showFailNotification(
                            'A Landing Page with the same name already exists.',
                            { level: LogLevel.INFO },
                        );
                    } else if (message.includes('slug already within this domain')) {
                        this.$helpers.showFailNotification(message, { level: LogLevel.INFO });
                    } else {
                        this.$helpers.showFailNotification(
                            'An error occurred trying to create the landing page.',
                            { error },
                        );
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        // Template

        async getLandingPageTemplateData() {
            const service = '/service/LandingPageTemplates';
            const { landingPageTemplateId, hasCategoriesEnabled } = this.widget;
            const queryFilters = [`$filter=Id eq ${landingPageTemplateId}`];
            if (hasCategoriesEnabled) {
                queryFilters.push('$expand=Categories($select=Name)');
            }
            try {
                const response = await this.$api.get(`${service}?${queryFilters.join('&')}`);
                this.setLandingPageTemplateInformation(response);
            } catch (error) {
                this.$helpers.showFailNotification('Failed to get landing page template info', {
                    error,
                });
            }
        },

        async setLandingPageTemplateInformation({ data }) {
            const [item] = data.value;
            this.form = {
                ...this.form,
                config: item.TemplateJson,
                favIconUrl: item.FaviconUrl,
                landingPageName: item.Name,
                metaTitle: item.MetaTitle,
                categories: item.Categories ? item.Categories.map(({ Name }) => Name) : [],
            };
        },

        // Edit

        async getEditLandingPageData() {
            const { landingPageId } = this.widget;
            const filter = `Id eq ${landingPageId}`;
            const expand =
                'Categories($select=Name),UpdatedByUser($select=FirstName,LastName,ProfileImageUrl)';
            try {
                const response = await LandingPageService.get(landingPageId, { filter, expand });
                this.setEditLandingPageInformation(response);
            } catch (error) {
                this.$helpers.showFailNotification('Failed to get landing page info', { error });
            }
        },

        setEditLandingPageInformation({ data }) {
            const [item] = data.value;
            this.form = {
                ...this.form,
                landingPageName: item.Name,
                metaTitle: item.MetaTitle,
                isPublished: item.Status === 'Published',
                slug: item.Slug,
                domain: { id: item.DomainId },
                categories: item.Categories.map(({ Name }) => Name),
                url: item.Url,
                favIconUrl: item.FaviconUrl,
                createdDate: this.$moment(item.CreatedAt),
                publishedDate: this.$moment(item.PublishedAt),
                hideFromSearchEngines: item.HideFromSearchEngines,
                updatedDate: this.$moment(item.UpdatedAt),
                modifiedBy: item.UpdatedByUser
                    ? {
                          name: `${item.UpdatedByUser.FirstName} ${item.UpdatedByUser.LastName}`,
                          profileImageUrl: item.UpdatedByUser.ProfileImageUrl,
                      }
                    : null,
            };

            this.sectionContext = {
                header: {
                    id: item.HeaderId,
                    hasChanges: false,
                },

                footer: {
                    id: item.FooterId,
                    hasChanges: false,
                },
            };
        },

        handlePatchLandingPage() {
            this.isLoading = true;
            const { landingPageId } = this.widget;

            const changes = [
                {
                    name: 'Name',
                    value: this.form.landingPageName,
                },
                {
                    name: 'MetaTitle',
                    value: this.form.metaTitle,
                },
                {
                    name: 'FaviconUrl',
                    value: this.form.favIconUrl,
                },
                {
                    name: 'HideFromSearchEngines',
                    value: this.form.hideFromSearchEngines,
                },
                {
                    name: 'HeaderId',
                    value: this.form.selectedHeader.value,
                },
                {
                    name: 'FooterId',
                    value: this.form.selectedFooter.value,
                },
                {
                    name: 'Slug',
                    value: this.form.slug,
                },
                {
                    name: 'DomainId',
                    value: this.form.domain.id,
                },
                {
                    name: 'Categories',
                    value: this.form.categories,
                },
            ];

            LandingPageService.patch(landingPageId, changes)
                .then(() => {
                    this.$helpers.showSuccessNotification('Landing page updated successfully.');
                    EventBus.$emit('landing-page:update');
                    this.closeWidget();
                    this.$router.push(`/LandingPages/${landingPageId}/Design`);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to update the landing page.', {
                        error,
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style>
.landing-page-settings-widget.widget .content-wrapper {
    padding: 30px 35px;
}

.landing-page-settings-widget .loading-screen {
    width: 100%;
    text-align: center;
}

.landing-page-settings-widget .widget-footer .actions-container {
    display: flex;
}

.landing-page-settings-widget .widget-footer .transparent-btn {
    background: transparent;
    color: #877d88;
}

.landing-page-settings-widget .widget-footer .transparent-btn:hover {
    background: #e1e0e5;
}

.landing-page-settings-widget .widget-footer .secondary-btn {
    background: transparent;
    color: #18aeff;
    margin-right: 30px;
}

.landing-page-settings-widget .widget-footer .secondary-btn:hover {
    background: #dbedfa;
}

.landing-page-settings-widget .widget-footer .secondary-btn span.fa {
    margin-right: 10px;
}

.landing-page-settings-widget .widget-footer .app-button:not(.secondary-btn) span.fa {
    margin-left: 10px;
}

/* common styles for all steps */

.landing-page-settings-widget .field {
    margin-bottom: 16px;
    font: normal normal normal 16px/21px Roboto;
}

.landing-page-settings-widget .field label:first-child:not(.checkbox-container) {
    margin-bottom: 13px;
}

.landing-page-settings-widget .field .field-error {
    margin-top: 5px;
    color: #f30070;
    display: block;
}

.landing-page-settings-widget .field .checkbox-container {
    display: flex;
}

.landing-page-settings-widget .field .checkbox-container .checkbox-label {
    margin-left: 16px;
    flex: 1;
}
</style>
